import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Widget, Box, Select, Radio, IconTextButton } from '@scope/scope-one-ui/core';
import Download from 'assets/icons/download.svg';
import { COLORS } from '@scope/scope-one-ui/theme';
import { useGetValueChainDistribution, useGetExportUrl } from '../../services/climateStressTest';
import { PageData, Scenario } from '../../types/climateStressTest';
import { ValueChainInfoCard } from './ValueChainInfoCard';

export const ValueChainDistribution: FC<PageData> = ({ entity, countries, sectors }) => {
  const { t } = useTranslation();

  const [sector, setSector] = useState(entity.sector.code);
  const [country, setCountry] = useState(entity.country.name);
  const [scenario, setScenario] = useState<Scenario>('orderly');

  const countryIso = countries.find((item) => item.name === country)?.iso!;

  const { data } = useGetValueChainDistribution(countryIso, sector, scenario);
  const exportFile = useGetExportUrl(
    'value-chain-distribution-of-output-losses',
    countryIso,
    sector,
  );

  const upstreamData = [
    {
      name: t('Supply chain input'),
      value: data?.upstreamSupplyChainInput || 0,
      color: COLORS.primary.main,
    },
    {
      name: t('Value added (labour/taxes)'),
      value: data?.upstreamValueAdded || 0,
      color: COLORS.warning.main,
    },
  ];

  const downstreamData = [
    {
      name: t('Intermediate demand use'),
      value: data?.downstreamIntermediateDemandUse || 0,
      color: COLORS.primary.main,
    },
    {
      name: t('Final demand use'),
      value: data?.downstreamFinalDemandUse || 0,
      color: COLORS.warning.main,
    },
  ];

  return (
    <Box display="flex" flexDirection="column" gridGap={2}>
      <Widget
        title={t('Value-chain distribution of output losses')}
        customHeader={
          <IconTextButton icon={<Download />} onClick={exportFile}>
            Export
          </IconTextButton>
        }
        hasFilters
      >
        <Box display="flex" justifyContent="space-between" p={2} gridGap={2}>
          <Radio
            value={scenario}
            onChange={(val) => setScenario(val as Scenario)}
            options={[
              { value: 'orderly', label: 'Orderly' },
              { value: 'disorderly', label: 'Disorderly' },
              { value: 'hotHouse', label: 'Hot House' },
            ]}
          />
          <Box display="flex" gridGap={2}>
            <Select
              value={sector}
              label={t('Sector')}
              onChange={(val) => setSector(val as string)}
              options={sectors.map(({ code, name }) => ({ value: code, label: name }))}
            />
            {!entity.isSovereign && (
              <Select
                value={country}
                label={t('Country')}
                onChange={(val) => setCountry(val as string)}
                options={countries.map(({ name }) => ({ value: name, label: name }))}
              />
            )}
          </Box>
        </Box>
      </Widget>
      <Box display="flex" gridGap={4}>
        <Box display="flex" flexDirection="column" flex={1}>
          <ValueChainInfoCard
            title="UPSTREAM"
            tooltip="Share of Upstream contribution (in %)"
            data={upstreamData}
          />
        </Box>
        <Box display="flex" flexDirection="column" flex={1}>
          <ValueChainInfoCard
            title="DOWNSTREAM"
            tooltip="Share of Downstream contribution (in %)"
            data={downstreamData}
          />
        </Box>
      </Box>
    </Box>
  );
};
